<template>
  <!-- A cena abissal, como na roda: escura e viva. A `naufragio` foi testada e rende faixas
       chapadas a 1080p, como recife-raso e mar-aberto — evitar como fundo de tela cheia. -->
  <FishScene cena="abissal" :escurecimento="0.4" class="caderno">
    <div class="caderno__interior">
      <div class="caderno__topo">
        <h1 class="caderno__titulo banner-titulo">{{ $t('inventory.caderno.titulo') }}</h1>
      </div>

      <!--
        6.1–6.6 — O CADERNO DO PESCADOR (D17, D3, D9). As seis abas azuis do inventário antigo viram
        marcadores de fita num caderno de verdade: a mochila e o cantinho numa dupla de páginas, a
        coleção e as conquistas paginadas com virada, o ranking como diário de bordo, e o calendário
        e os torneios numa folha avulsa (conteúdo largo não cabe em meia página — 7.22 ainda deve o
        calendário repensado).
      -->
      <FishBook
        class="caderno__livro"
        :marcadores="marcadores"
        :marcador="fita"
        :rotulo-dos-marcadores="$t('inventory.caderno.titulo')"
        :tem-anterior="temAnterior"
        :tem-proxima="temProxima"
        :paginacao="paginacao"
        @update:marcador="trocarFita"
        @virar="virarPagina"
      >
        <!-- ==================== MOCHILA: a grade que cresce (D17) ==================== -->
        <template #esquerda>
          <div v-if="fita === 'mochila'" class="caderno__pagina">
            <p class="caderno__rotulo">{{ $t('inventory.caderno.mochila') }}</p>

            <EmptyState
              v-if="!entradas.length"
              icone="nav-inventario"
              :titulo="$t('inventory.caderno.mochilaVazia')"
              :explicacao="$t('inventory.caderno.mochilaVaziaExplicacao')"
              :acao="$t('inventory.caderno.irParaVila')"
              icone-da-acao="nav-loja"
              @acao="$router.push('/shop')"
            />

            <FishGrid v-else :total="entradas.length" :colunas-telefone="4" :colunas-desktop="4">
              <FishSlot
                v-for="entrada in entradas"
                :key="entrada.chave"
                :item="entrada.item"
                :quantidade="entrada.quantidade"
                :equipado="entrada.equipado"
                :requisito="entrada.requisito"
                :selecionado="selecionada && selecionada.chave === entrada.chave"
                :arrastando="ehOrigem(entrada.chave)"
                @selecionar="tocarNaEntrada(entrada)"
                @equipar="equiparDireto(entrada)"
                @mousedown="pressionar($event, entrada)"
                @touchstart="pressionar($event, entrada)"
              />
            </FishGrid>
          </div>

          <!-- ============= ENCICLOPÉDIA: o caderno de naturalista (D14) ============= -->
          <!--
            A arte do peixe vai DIRETO no papel, como desenho de campo — nada de encaixe escuro
            engolindo o peixe ("cadê a foto?", o autor, 03/09). Nome e contagem embaixo, a lápis.

            8.4–8.6 — Era uma segunda vista do saco de peixes por vender, que
            a Peixaria já mostra e onde a venda de fato acontece; virou a estante do naturalista: o
            catálogo INTEIRO, silhueta no que falta, e o contador de descobertos no rótulo.
          -->
          <div v-else-if="fita === 'enciclopedia'" class="caderno__pagina">
            <!--
              NO TELEFONE não há dupla: a ficha toma a página da estante, com o voltar no rótulo.
              É o gesto do próprio caderno — vira a folha e volta — em vez de um modal por cima
              dele, que empilharia papel sobre papel.
            -->
            <template v-if="umaPagina && entrada.aberta">
              <p class="caderno__rotulo">
                <button type="button" class="caderno__voltar" @click="fecharEntrada">
                  ‹ {{ $t('inventory.enciclopedia.voltarAEstante') }}
                </button>
              </p>
              <FichaDoPeixe :entrada="entrada" @ir-para-o-mapa="irParaOMapa" />
            </template>

            <template v-else>
              <p class="caderno__rotulo">
                {{ $t('inventory.enciclopedia.titulo') }}
                <span v-if="enciclopedia.total" class="caderno__contador t-numero">
                  {{ enciclopedia.descobertos }} / {{ enciclopedia.total }}
                </span>
              </p>

              <EmptyState
                v-if="!estante.length"
                icone="fish"
                :titulo="$t(carregandoEnciclopedia ? 'inventory.enciclopedia.carregando' : 'fishCollection.emptyMessage')"
                :acao="carregandoEnciclopedia ? '' : $t('inventory.caderno.irPescar')"
                icone-da-acao="nav-pescar"
                @acao="$router.push('/fishing')"
              />
              <PaginaDaEnciclopedia
                v-else
                :peixes="paginaDePeixes(indicePagina)"
                :aberto="entrada.dados ? entrada.dados.id : null"
                @abrir="abrirEntrada"
              />
            </template>
          </div>

          <!-- ==================== CONQUISTAS: selos no caderno ==================== -->
          <div v-else-if="fita === 'conquistas'" class="caderno__pagina">
            <ul class="caderno__selos">
              <li
                v-for="selo in paginaDeConquistas(indicePagina)"
                :key="selo.titulo"
                :class="{ 'caderno__selo--fechado': !selo.alcancada }"
              >
                <img :src="selo.imagem" :alt="selo.titulo" />
                <div class="crescer">
                  <p class="caderno__selo-nome">{{ $t(`rewards.titles.${selo.titulo}`) }}</p>
                  <p class="caderno__selo-descricao">{{ selo.descricao }}</p>
                </div>
                <FishIcon v-if="selo.alcancada" nome="check" tamanho="pequeno" />
              </li>
            </ul>
          </div>

          <!-- ==================== RANKING: o diário de bordo ==================== -->
          <div v-else-if="fita === 'ranking'" class="caderno__pagina">
            <PaginaDeRanking v-if="rankings[indicePagina]" :ranking="rankings[indicePagina]" />
          </div>

          <!-- ==================== CALENDÁRIO: a agenda do mês (7.22) ==================== -->
          <div v-else-if="fita === 'calendario'" class="caderno__pagina">
            <CalendarEvents @selecionar="selecionarEvento" />
          </div>

          <!-- ==================== TORNEIOS: um cartaz por página ==================== -->
          <div v-else-if="fita === 'torneios'" class="caderno__pagina">
            <p class="caderno__rotulo">{{ $t('competitions.title') }}</p>
            <TorneioCartaz
              v-if="competitions[indicePagina]"
              :competition="competitions[indicePagina]"
              :ranking="rankingDoTorneio(competitions[indicePagina].id)"
            />
            <p v-else class="caderno__ficha-vazia">{{ $t('competitions.noCompetitions') }}</p>
          </div>
        </template>

        <template #direita>
          <div v-if="fita === 'mochila'" class="caderno__pagina">
            <p class="caderno__rotulo">{{ $t('inventory.caderno.cantinho') }}</p>

            <!--
              O cantinho NÃO é um boneco (D17): três encaixes num corpo pareceriam falta. São os
              objetos do pescador — a vara, o barco, a chave da casa — e a lata de isca, que não é
              encaixe: isca é consumível, o que existe é a lata com a contagem.
            -->
            <div class="caderno__cantinho">
              <div v-for="lugar in cantinho" :key="lugar.slot" class="caderno__lugar">
                <FishSlot
                  :item="lugar.item"
                  :quantidade="lugar.quantidade"
                  :esboco="lugar.esboco"
                  :aceso="aceita(lugar.slot)"
                  :data-encaixe="lugar.equipavel ? lugar.slot : null"
                  @selecionar="tocarNoLugar(lugar)"
                />
                <span class="caderno__lugar-nome">{{ lugar.rotulo }}</span>
              </div>
            </div>

            <!-- A ficha do que foi tocado: é também o caminho SEM arrastar (D11) — o botão equipa. -->
            <div class="caderno__ficha">
              <template v-if="selecionada">
                <p class="caderno__ficha-nome">{{ selecionada.item.nome }}</p>
                <ul class="caderno__ficha-meta">
                  <li v-for="linha in fichaMeta" :key="linha">{{ linha }}</li>
                </ul>
                <FishButton
                  v-if="acaoDaFicha"
                  variante="primario"
                  tamanho="pequeno"
                  bloco
                  :carregando="agindo"
                  @click="agir"
                >
                  {{ acaoDaFicha }}
                </FishButton>
              </template>
              <p v-else class="caderno__ficha-vazia">{{ $t('inventory.caderno.ficha') }}</p>
            </div>

            <!-- Os atributos, como notas de campo — não um painel azul de estatísticas. -->
            <ul class="caderno__notas">
              <li v-for="nota in notasDeAtributos" :key="nota.icone">
                <FishIcon :nome="nota.icone" tamanho="pequeno" />
                <span class="crescer">{{ nota.rotulo }}</span>
                <strong class="t-numero">{{ nota.valor }}</strong>
              </li>
            </ul>

            <!-- Os apetrechos da oficina (anzol, boia, caixa): três encaixes próprios, fora do
                 cantinho, porque vêm de outro lugar (a exploração) e têm outro verbo no servidor. -->
            <Apetrechos />
          </div>

          <!--
            A PÁGINA DA DIREITA É A FICHA (D14, D3). O caderno tem duas páginas; gastar as duas com
            a mesma grade e abrir a ficha num modal por cima seria pôr papel sobre papel — e o
            modal ainda tapa a estante, que é justamente o que o jogador quer continuar vendo
            enquanto compara.

            A estante fica só na esquerda, então a virada anda UMA página por vez aqui — ver
            `passo`. Trinta peixes viram cinco folhas em vez de três, e é troca boa: a ficha
            aberta ao lado vale mais que duas viradas a menos.
          -->
          <div v-else-if="fita === 'enciclopedia' && !umaPagina" class="caderno__pagina">
            <FichaDoPeixe v-if="entrada.aberta" :entrada="entrada" @ir-para-o-mapa="irParaOMapa" />

            <!-- Nada escolhido: a página fica em branco com a instrução, como um caderno de campo
                 esperando a próxima anotação. -->
            <div v-else class="caderno__ficha-vazia enciclopedia__convite">
              {{ $t('inventory.enciclopedia.escolhaUmPeixe') }}
            </div>
          </div>

          <div v-else-if="fita === 'conquistas' && !umaPagina" class="caderno__pagina">
            <ul class="caderno__selos">
              <li
                v-for="selo in paginaDeConquistas(indicePagina + 1)"
                :key="selo.titulo"
                :class="{ 'caderno__selo--fechado': !selo.alcancada }"
              >
                <img :src="selo.imagem" :alt="selo.titulo" />
                <div class="crescer">
                  <p class="caderno__selo-nome">{{ $t(`rewards.titles.${selo.titulo}`) }}</p>
                  <p class="caderno__selo-descricao">{{ selo.descricao }}</p>
                </div>
                <FishIcon v-if="selo.alcancada" nome="check" tamanho="pequeno" />
              </li>
            </ul>
          </div>

          <div v-else-if="fita === 'ranking' && !umaPagina" class="caderno__pagina">
            <PaginaDeRanking v-if="rankings[indicePagina + 1]" :ranking="rankings[indicePagina + 1]" />
          </div>

          <!-- O detalhe do evento tocado na agenda: a página ao lado, como a ficha da mochila. -->
          <div v-else-if="fita === 'calendario'" ref="detalheDoEvento" class="caderno__pagina">
            <template v-if="eventoSelecionado">
              <p class="caderno__rotulo">{{ textoDoEvento(eventoSelecionado.event.name, 'name', eventoSelecionado.event.name) }}</p>
              <img class="caderno__evento-arte" :src="imagemDoEvento(eventoSelecionado.event.name)" :alt="textoDoEvento(eventoSelecionado.event.name, 'name', eventoSelecionado.event.name)" />
              <p class="caderno__evento-texto">{{ textoDoEvento(eventoSelecionado.event.name, 'description', eventoSelecionado.event.description) }}</p>
              <ul class="caderno__ficha-meta">
                <li v-if="eventoSelecionado.event.effectType">
                  {{ $t('events.calendar.effectType') }}: {{ $t(`events.calendar.effectTypes.${eventoSelecionado.event.effectType}`) }}
                </li>
                <li v-if="eventoSelecionado.event.effectNumber">
                  {{ $t('events.calendar.effectNumber') }}: {{ eventoSelecionado.event.effectNumber }}%
                </li>
                <li>{{ $t('events.calendar.remainingDays') }}: {{ diasRestantes(eventoSelecionado.endDate) }}</li>
                <li>
                  {{ $t('events.calendar.period') }}:
                  {{ new Date(eventoSelecionado.createdAt).toLocaleDateString() }} –
                  {{ new Date(eventoSelecionado.endDate).toLocaleDateString() }}
                </li>
              </ul>
            </template>
            <p v-else class="caderno__ficha-vazia">{{ $t('inventory.caderno.eventoDica') }}</p>
          </div>

          <div v-else-if="fita === 'torneios' && !umaPagina" class="caderno__pagina caderno__pagina--continuacao">
            <TorneioCartaz
              v-if="competitions[indicePagina + 1]"
              :competition="competitions[indicePagina + 1]"
              :ranking="rankingDoTorneio(competitions[indicePagina + 1].id)"
            />
          </div>
        </template>
      </FishBook>
    </div>

    <!-- O fantasma do arrasto: segue o dedo/mouse, deslocado para cima (o dedo cobre o item). -->
    <div
      v-if="fantasma.visivel && itemLevantado"
      class="caderno__fantasma"
      :style="{ left: `${fantasma.x}px`, top: `${fantasma.y}px` }"
    >
      <img :src="itemLevantado.item.imagem" :alt="itemLevantado.item.nome" />
    </div>

    <!-- O baú premium: sem círculos mágicos — um baú, um clique, um prêmio no papel. -->
    <FishModal :aberto="bau.aberto" :titulo="$t('inventory.openChest')" @fechar="fecharBau">
      <div class="caderno__bau">
        <button
          v-if="!bau.premio"
          type="button"
          class="caderno__bau-fechado"
          :disabled="bau.abrindo"
          @click="abrirBau"
        >
          <img src="@/assets/images/chests/Icon11.png" :alt="$t('inventory.openChest')" />
          <span>{{ $t('inventory.lootbox.prompt') }}</span>
        </button>

        <div v-else class="caderno__bau-premio">
          <img src="@/assets/images/chests/Icon12.png" alt="" aria-hidden="true" />
          <p class="caderno__bau-nome">{{ bau.premio.name }}</p>
          <p v-if="bau.premio.amount > 1" class="t-numero caderno__bau-quanto">{{ bau.premio.amount }}x</p>
          <FishButton variante="primario" som="recompensa" bloco @click="fecharBau">
            {{ $t('inventory.lootbox.receive') }}
          </FishButton>
        </div>
      </div>
    </FishModal>
  </FishScene>
</template>

<script>
import { defineComponent, h } from 'vue';
import { mapState, mapGetters } from 'vuex';
import { FishScene, FishBook, FishGrid, FishSlot, FishButton, FishIcon, FishModal, EmptyState } from '@/shared/ui';
import arrasto from '@/shared/ui/useDragDrop';
import CalendarEvents from '@/shared/inventory/CalendarEvents.vue';
import TorneioCartaz from '@/shared/inventory/TorneioCartaz.vue';
import FichaDoPeixe from '@/shared/inventory/FichaDoPeixe.vue';
import Apetrechos from '@/shared/pescador/Apetrechos.vue';
import { arteDoApetrecho } from '@/shared/pescador/arteDoApetrecho';
import { raridadePt } from '@/shared/utils/raridade';
import { rewardImage } from '@/shared/utils/rewardImages';
import { getRewardDescription } from '@/shared/utils/rewardDescription';
import { getItemImage } from '@/shared/utils/itemImages';
import { getFishImage } from '@/shared/utils/fishesImages';
import { getItemImage as imagemDeEvento } from '@/shared/utils/eventImages';
import { getMapKey } from '@/shared/utils/mapNames';
import * as v2 from '@/core/services/api/v2';

/**
 * Quantos peixes por página da estante (2 colunas × 4 desenhos).
 *
 * Eram 6 quando a lista usava as duas folhas. Agora ela usa só a esquerda — a direita é a ficha —
 * e 6 deixaria 30 peixes em cinco viradas. Oito cabem porque o livro cresceu no desktop, e no
 * telefone a página rola.
 */
const PEIXES_POR_PAGINA = 8;
/** Quantos selos de conquista por página. */
const SELOS_POR_PAGINA = 4;

/**
 * Uma página de ranking do diário de bordo. Componente local porque as três listas (ricos, nível,
 * peixes) têm a MESMA forma e só muda o número mostrado — e porque uma página do caderno não
 * justifica um arquivo próprio.
 */
const PaginaDeRanking = defineComponent({
  name: 'PaginaDeRanking',
  props: { ranking: { type: Object, required: true } },
  render() {
    const linhas = this.ranking.jogadores.map((jogador, i) =>
      h('li', { key: jogador.position, class: { 'caderno__posicao--laureada': i < 3 } }, [
        h('span', { class: 'caderno__posicao-numero t-numero' }, `${jogador.position}.`),
        h(
          'a',
          {
            class: 'caderno__posicao-nome',
            href: `/profile/${jogador.username}`,
            onClick: (e) => {
              e.preventDefault();
              this.$router.push(`/profile/${jogador.username}`);
            },
          },
          jogador.username,
        ),
        h('span', { class: 'caderno__posicao-valor t-numero' }, this.ranking.valor(jogador)),
      ]),
    );
    return h('div', [
      h('p', { class: 'caderno__posicoes-rotulo' }, this.ranking.titulo),
      linhas.length
        ? h('ol', { class: 'caderno__posicoes' }, linhas)
        : h('p', { class: 'caderno__ficha-vazia' }, this.$t('rankings.noPlayers')),
    ]);
  },
});

/**
 * Uma página da estante (D14). Componente local pela mesma razão do `PaginaDeRanking`: a dupla de
 * páginas mostra a MESMA lista duas vezes, com índices diferentes, e duplicar seis linhas de
 * markup é como o nome do peixe acabaria certo numa página e errado na outra.
 *
 * O peixe não descoberto é um `li` sem botão: a silhueta não abre nada, e um botão desabilitado
 * convidaria ao clique para depois recusá-lo.
 */
const PaginaDaEnciclopedia = defineComponent({
  name: 'PaginaDaEnciclopedia',
  props: {
    peixes: { type: Array, required: true },
    /** O id do peixe cuja ficha está aberta na outra página, para marcá-lo aqui. */
    aberto: { type: Number, default: null },
  },
  emits: ['abrir'],
  render() {
    const desenho = (peixe) => [
      h('img', {
        class: ['caderno__peixe-arte', { 'caderno__peixe-arte--silhueta': !peixe.descoberto }],
        src: peixe.imagem,
        alt: peixe.nome,
        // A silhueta não é informação para quem lê a tela por voz — o nome já diz "por descobrir".
        'aria-hidden': peixe.descoberto ? null : 'true',
      }),
      h('p', { class: 'caderno__peixe-nome' }, peixe.nome),
      h(
        'p',
        { class: 'caderno__peixe-medida t-numero' },
        peixe.descoberto ? `${peixe.quantosPegou}×` : peixe.ondeProcurar,
      ),
    ];

    return h(
      'ul',
      { class: 'caderno__peixes' },
      this.peixes.map((peixe) =>
        h('li', { key: peixe.id }, [
          peixe.descoberto
            ? h(
                'button',
                {
                  type: 'button',
                  class: [
                    'caderno__peixe-botao',
                    // O que está aberto na outra página fica marcado aqui: sem isso, a ficha à
                    // direita e a grade à esquerda seriam duas coisas sem ligação visível.
                    { 'caderno__peixe-botao--aberto': peixe.id === this.aberto },
                  ],
                  'aria-current': peixe.id === this.aberto ? 'true' : null,
                  onClick: () => this.$emit('abrir', peixe),
                },
                desenho(peixe),
              )
            : h('div', { class: 'caderno__peixe-botao caderno__peixe-botao--mudo' }, desenho(peixe)),
        ]),
      ),
    );
  },
});

export default {
  name: 'InventoryPage',
  components: {
    FishScene,
    FishBook,
    FishGrid,
    FishSlot,
    FishButton,
    FishIcon,
    FishModal,
    EmptyState,
    CalendarEvents,
    TorneioCartaz,
    FichaDoPeixe,
    Apetrechos,
    PaginaDeRanking,
    PaginaDaEnciclopedia,
  },
  data() {
    return {
      fita: 'mochila',
      /** A página aberta em cada seção paginada — trocar de fita não perde o lugar. */
      paginas: { enciclopedia: 0, conquistas: 0, ranking: 0, torneios: 0 },
      /** A estante do D14. Vem do servidor: descoberta e recorde são dado de jogador, não do bundle. */
      enciclopedia: { descobertos: 0, total: 0, itens: [] },
      enciclopediaCarregada: false,
      carregandoEnciclopedia: false,
      /** A entrada aberta. `dados` nulo enquanto o servidor não responde. */
      entrada: { aberta: false, carregando: false, dados: null },
      umaPagina: false,
      selecionada: null,
      eventoSelecionado: null,
      agindo: false,
      conquistasAlcancadas: [],
      conquistasCarregadas: false,
      torneiosRelogio: null,
      bau: { aberto: false, abrindo: false, premio: null, entrada: null },
    };
  },
  computed: {
    ...mapState('player', ['inventory', 'equipment', 'stats', 'stamina', 'richestPlayers', 'highestLevelPlayers', 'mostActiveFishers']),
    ...mapState('competition', ['competitions']),
    ...mapGetters('competition', ['getCompetitionRanking']),
    ...mapState('pescador', ['pocoes']),
    ...mapGetters('pescador', ['pilhasDeApetrechos']),
    marcadores() {
      return [
        { id: 'mochila', rotulo: this.$t('inventory.caderno.mochila') },
        { id: 'enciclopedia', rotulo: this.$t('inventory.enciclopedia.fita') },
        { id: 'conquistas', rotulo: this.$t('rewards.title') },
        { id: 'calendario', rotulo: this.$t('inventory.calendar') },
        { id: 'torneios', rotulo: this.$t('competitions.title') },
        { id: 'ranking', rotulo: this.$t('inventory.ranking') },
      ];
    },

    /* ------------------------------------------------------------- mochila */

    /**
     * Tudo o que o pescador carrega, numa grade só (D17): varas, barcos, casas — com o `equipado`
     * marcado, porque o baú do V2 lista TODAS as peças — e os empilháveis (iscas, tônicos, baús)
     * com a quantidade no canto.
     *
     * **Apetrechos e poções entraram em 11/09/2026.** Até ali a grade lia só `GET inventory` (o
     * que vem da loja), e o que sai da OFICINA mora em outras duas rotas — o autor craftou uma
     * boia, abriu a mochila e perguntou "cadê?". Ninguém tinha decidido deixá-los de fora: o
     * caderno é mais antigo que a oficina do pescador. O apetrecho entra em PILHA (três boias
     * iguais são "×3"; ver `pilhasDeApetrechos`), com a equipada numa pilha própria e o selo.
     */
    entradas() {
      const inv = this.inventory || {};
      const peca = (bruto, tipo, alvo) => ({
        chave: `${tipo}-${bruto.id}`,
        tipo,
        alvo,
        equipado: bruto.equipped === true,
        quantidade: 1,
        // Não há "Quebrada": a vara que zera some do baú no servidor (13/09/2026).
        requisito: '',
        bruto,
        item: { id: bruto.id, nome: bruto.name, imagem: bruto.image || getItemImage(bruto.name), rarity: bruto.rarity },
      });

      return [
        ...(inv.rods || []).map((r) => peca(r, 'vara', 'vara')),
        ...(inv.boats || []).map((b) => peca(b, 'barco', 'barco')),
        ...(inv.houses || []).map((c) => peca(c, 'casa', 'casa')),
        ...(this.pilhasDeApetrechos || []).map((pilha) => ({
          chave: `apetrecho-${pilha.chave}`,
          tipo: 'apetrecho',
          // Sem `alvo`: os encaixes do apetrecho moram no componente ao lado, não no cantinho,
          // e o arrasto só conhece os do cantinho. O gesto é o botão da ficha (D11).
          alvo: null,
          equipado: pilha.equipado,
          quantidade: pilha.quantidade,
          requisito: '',
          bruto: pilha,
          item: {
            id: pilha.chave,
            nome: pilha.nome,
            imagem: arteDoApetrecho(pilha),
            raridade: raridadePt(pilha.raridade),
          },
        })),
        ...(inv.baits || [])
          .filter((isca) => (isca.quantity ?? 0) > 0)
          .map((isca) => ({
            chave: `isca-${isca.id}`,
            tipo: 'isca',
            alvo: null,
            equipado: false,
            quantidade: isca.quantity,
            requisito: '',
            bruto: isca,
            item: { id: isca.id, nome: isca.name, imagem: isca.image || getItemImage(isca.name) },
          })),
        ...(this.pocoes || [])
          .filter((pocao) => pocao.quantidade > 0)
          .map((pocao) => ({
            chave: `pocao-${pocao.potionId}`,
            tipo: 'pocao',
            alvo: null,
            equipado: false,
            quantidade: pocao.quantidade,
            requisito: '',
            bruto: pocao,
            item: {
              id: pocao.potionId,
              nome: pocao.nome,
              imagem: pocao.imagem,
              icone: 'flask',
              raridade: raridadePt(pocao.raridade),
            },
          })),
        ...(inv.others || [])
          .filter((outro) => (outro.quantity ?? 0) > 0)
          .map((outro) => ({
            chave: `outro-${outro.id}`,
            tipo: outro.type, // 'tonic' | 'lootbox'
            alvo: null,
            equipado: false,
            quantidade: outro.quantity,
            requisito: '',
            bruto: outro,
            item: { id: outro.id, nome: outro.name, imagem: outro.image || getItemImage(outro.name) },
          })),
      ];
    },

    /** Os quatro lugares do cantinho. A lata de isca mostra a MELHOR isca e o total de todas. */
    cantinho() {
      const eq = this.equipment || {};
      const iscas = (this.inventory?.baits || []).filter((isca) => (isca.quantity ?? 0) > 0);
      const melhorIsca = [...iscas].sort((a, b) => (b.fishingPower || 0) - (a.fishingPower || 0))[0];
      const totalDeIscas = iscas.reduce((total, isca) => total + isca.quantity, 0);
      const como = (peca) =>
        peca ? { id: peca.id, nome: peca.name, imagem: peca.image || getItemImage(peca.name) } : null;

      return [
        { slot: 'vara', rotulo: this.$t('inventory.caderno.vara'), esboco: 'slot-vara', equipavel: true, item: como(eq.rod), quantidade: 1 },
        { slot: 'barco', rotulo: this.$t('inventory.caderno.barco'), esboco: 'slot-barco', equipavel: true, item: como(eq.boat), quantidade: 1 },
        { slot: 'casa', rotulo: this.$t('inventory.caderno.casa'), esboco: 'slot-casa', equipavel: true, item: como(eq.house), quantidade: 1 },
        { slot: 'iscas', rotulo: this.$t('inventory.caderno.iscas'), esboco: 'slot-isca', equipavel: false, item: como(melhorIsca ? { ...melhorIsca, image: melhorIsca.image } : null), quantidade: totalDeIscas || 1 },
      ];
    },

    notasDeAtributos() {
      const stats = this.stats || {};
      return [
        { icone: 'atributo-poder', rotulo: this.$t('inventory.stats.fishingPower'), valor: `${Math.round(stats.fishingPower || 0)}` },
        { icone: 'atributo-captura', rotulo: this.$t('inventory.stats.catchRate'), valor: `${((stats.catchRate || 0) * 100).toFixed(0)}%` },
        { icone: 'atributo-critico', rotulo: this.$t('inventory.stats.criticalRate'), valor: `${((stats.criticalRate || 0) * 100).toFixed(1)}%` },
        { icone: 'atributo-recolhimento', rotulo: this.$t('inventory.stats.reelSpeed'), valor: `${(stats.reelSpeed || 1).toFixed(1)}x` },
        { icone: 'atributo-energia', rotulo: this.$t('inventory.stats.rechargeStamina'), valor: `${this.stamina?.rechargeStamina ?? 0}` },
      ];
    },

    fichaMeta() {
      const e = this.selecionada;
      if (!e) return [];
      const t = (chave) => this.$t(`inventory.caderno.${chave}`);
      const linhas = [];
      if (e.tipo === 'vara') {
        if (e.bruto.power != null) linhas.push(`${t('poder')}: ${e.bruto.power}`);
        if (e.bruto.durability) linhas.push(`${t('durabilidade')}: ${e.bruto.durability.current}/${e.bruto.durability.maxDurability}`);
      }
      if (e.tipo === 'barco' && e.bruto.speed != null) linhas.push(`${t('velocidade')}: ${e.bruto.speed}`);
      if (e.tipo === 'casa' && e.bruto.rechargeStamina != null) linhas.push(`${t('recarga')}: ${e.bruto.rechargeStamina}`);
      if (e.tipo === 'isca' && e.bruto.fishingPower != null) linhas.push(`${t('poder')}: ${e.bruto.fishingPower}`);
      if (e.quantidade > 1) linhas.push(`${t('quantidade')}: ${e.quantidade}`);
      if ((e.tipo === 'tonic' || e.tipo === 'lootbox') && e.bruto.tooltipText) linhas.push(e.bruto.tooltipText);
      // As mesmas frases da oficina e do mercado: quem aprendeu a ler lá lê igual aqui.
      if (e.tipo === 'apetrecho') {
        linhas.unshift(this.$t(`exploration.apetrechos.slots.${e.bruto.slot}`));
        for (const [chave, valor] of Object.entries(e.bruto.bonus || {})) {
          if (valor > 0) linhas.push(this.$t(`exploration.workshop.apetrechoBonus.${chave}`, { valor }));
        }
      }
      if (e.tipo === 'pocao') {
        linhas.unshift(
          this.$t(`exploration.workshop.pocaoEfeito.${e.bruto.efeito}`, {
            valor: e.bruto.valor,
            minutos: e.bruto.duracaoMin,
          }),
        );
      }
      return linhas;
    },

    /** O que o botão da ficha faz — o caminho sem arrastar do D11, e as ações de consumível. */
    acaoDaFicha() {
      const e = this.selecionada;
      if (!e) return '';
      if (e.tipo === 'equipado') return this.$t('inventory.caderno.guardar');
      if (e.alvo) return e.equipado ? this.$t('inventory.caderno.guardar') : this.$t('inventory.actions.equip');
      if (e.tipo === 'apetrecho') {
        return e.equipado ? this.$t('inventory.caderno.guardar') : this.$t('inventory.actions.equip');
      }
      if (e.tipo === 'pocao') return this.$t('exploration.pocoes.beber');
      if (e.tipo === 'tonic') return this.$t('inventory.useTonic');
      if (e.tipo === 'lootbox') return this.$t('inventory.openChest');
      return '';
    },

    /* --------------------------------------------------------- as páginas */

    /**
     * A estante pronta para desenhar (D14).
     *
     * O **nome** é traduzido aqui e não vem do servidor: ele já mora em
     * `i18n/locales/<idioma>/fishes.json`, e um segundo dono para o mesmo dado é exatamente como
     * `luck` acabou valendo 7,5 de um lado e 4,5 do outro. O servidor manda `nome: null` no não
     * descoberto — é o sinal de silhueta, não a ausência de tradução.
     *
     * Os mapas aparecem no não descoberto de propósito: é o que dá ao caderno a função de dizer
     * ONDE procurar o que falta.
     */
    estante() {
      return (this.enciclopedia.itens || []).map((item) => ({
        id: item.id,
        descoberto: item.descoberto,
        /*
         * `getFishImage` e não `getItemImage`: são dois mapas diferentes, e o de itens não tem peixe
         * NENHUM — a arte do peixe sempre veio do servidor, então usá-lo mandava os 30 para o
         * fallback de uma vez. E `arte` e não `nome`, porque o não descoberto tem nome nulo: sem a
         * chave, seriam trinta silhuetas idênticas no lugar de trinta formas.
         */
        imagem: item.imagem || getFishImage(item.arte),
        nome: item.descoberto
          ? this.nomeDoPeixe({ name: item.nome })
          : this.$t('inventory.enciclopedia.porDescobrir'),
        quantosPegou: item.quantosPegou,
        ondeProcurar: (item.mapas || []).map((m) => this.nomeDoMapa(m.nome)).join(' · ') || '—',
      }));
    },
    rankings() {
      const valorDe = {
        richest: (j) => `${new Intl.NumberFormat('pt-BR').format(j.gold)}`,
        highestLevel: (j) => `${this.$t('rankings.level')} ${j.level}`,
        mostFishes: (j) => `${new Intl.NumberFormat('pt-BR').format(j.totalFishCaught)}`,
      };
      return [
        { titulo: this.$t('rankings.sections.richest'), jogadores: this.richestPlayers || [], valor: valorDe.richest },
        { titulo: this.$t('rankings.sections.highestLevel'), jogadores: this.highestLevelPlayers || [], valor: valorDe.highestLevel },
        { titulo: this.$t('rankings.sections.mostFishes'), jogadores: this.mostActiveFishers || [], valor: valorDe.mostFishes },
      ];
    },
    conquistas() {
      return Object.entries(rewardImage).map(([titulo, imagem]) => ({
        titulo,
        imagem,
        alcancada: this.conquistasAlcancadas.includes(titulo),
        descricao: getRewardDescription(titulo, this.$i18n.locale),
      }));
    },

    totalDePaginas() {
      if (this.fita === 'enciclopedia') return Math.max(1, Math.ceil(this.estante.length / PEIXES_POR_PAGINA));
      if (this.fita === 'conquistas') return Math.max(1, Math.ceil(this.conquistas.length / SELOS_POR_PAGINA));
      if (this.fita === 'ranking') return this.rankings.length;
      if (this.fita === 'torneios') return Math.max(1, (this.competitions || []).length);
      return 1;
    },
    indicePagina() {
      return this.paginas[this.fita] ?? 0;
    },
    passo() {
      /*
       * A enciclopédia anda UMA página por vez mesmo na dupla: a estante mora só na esquerda,
       * porque a direita é a ficha. As outras seções usam as duas folhas para a mesma lista, e
       * viram duas de cada vez.
       */
      if (this.fita === 'enciclopedia') return 1;
      return this.umaPagina ? 1 : 2;
    },
    temAnterior() {
      return this.indicePagina > 0 && this.totalDePaginas > 1;
    },
    temProxima() {
      return this.indicePagina + this.passo < this.totalDePaginas && this.totalDePaginas > 1;
    },
    paginacao() {
      if (!['enciclopedia', 'conquistas', 'ranking', 'torneios'].includes(this.fita) || this.totalDePaginas <= 1) return '';
      const primeira = this.indicePagina + 1;
      const ultima = Math.min(this.indicePagina + this.passo, this.totalDePaginas);
      return primeira === ultima ? `${primeira} / ${this.totalDePaginas}` : `${primeira}–${ultima} / ${this.totalDePaginas}`;
    },

    /* ------------------------------------------------------------ arrasto */

    fantasma() {
      return arrasto.estado.fantasma;
    },
    itemLevantado() {
      return arrasto.estado.levantado;
    },
  },
  created() {
    this.medir();
  },
  async mounted() {
    window.addEventListener('resize', this.medir, { passive: true });
    // O arrasto precisa acompanhar o ponteiro pela tela toda, não só por cima dos encaixes.
    window.addEventListener('mousemove', this.moverArrasto, { passive: false });
    window.addEventListener('mouseup', this.largarArrasto);
    window.addEventListener('touchmove', this.moverArrasto, { passive: false });
    window.addEventListener('touchend', this.largarArrasto);

    // As poções entram na grade; os apetrechos o componente `Apetrechos` ao lado já carrega.
    this.$store.dispatch('pescador/carregarPocoes').catch(() => {});

    // O comportamento herdado da tela antiga: a primeira vara utilizável se equipa sozinha.
    await this.$store.dispatch('player/autoEquipRod');
    await this.$store.dispatch('player/autoEquipBoat');
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.medir);
    window.removeEventListener('mousemove', this.moverArrasto);
    window.removeEventListener('mouseup', this.largarArrasto);
    window.removeEventListener('touchmove', this.moverArrasto);
    window.removeEventListener('touchend', this.largarArrasto);
    arrasto.largar();
    if (this.torneiosRelogio) clearInterval(this.torneiosRelogio);
  },
  methods: {
    /**
     * O evento tocado na agenda. No telefone o detalhe mora DEPOIS da agenda, na mesma folha: sem
     * rolar até ele, tocar no evento não mudava nada à vista.
     */
    selecionarEvento(entrada) {
      this.eventoSelecionado = entrada;
      if (!this.umaPagina) return;
      this.$nextTick(() => {
        this.$refs.detalheDoEvento?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      });
    },
    medir() {
      // O mesmo breakpoint do FishBook (600px): o passo da virada é 2 páginas na dupla, 1 na única.
      this.umaPagina = !window.matchMedia('(min-width: 600px)').matches;
    },

    trocarFita(id) {
      this.fita = id;
      this.selecionada = null;
      this.eventoSelecionado = null;
      arrasto.largar();

      // As seções que falam com o servidor carregam quando abertas, não antes.
      if (id === 'ranking' && !(this.richestPlayers || []).length) {
        this.$store.dispatch('player/fetchRichestPlayers');
        this.$store.dispatch('player/fetchHighestLevelPlayers');
        this.$store.dispatch('player/fetchMostActiveFishers');
      }
      if (id === 'conquistas' && !this.conquistasCarregadas) {
        this.carregarConquistas();
      }
      if (id === 'enciclopedia' && !this.enciclopediaCarregada) {
        this.carregarEnciclopedia();
      }

      // Os torneios têm relógio: o tempo restante muda enquanto a página está aberta.
      if (id === 'torneios') {
        this.carregarTorneios();
        if (!this.torneiosRelogio) {
          this.torneiosRelogio = setInterval(this.carregarTorneios, 60000);
        }
      } else if (this.torneiosRelogio) {
        clearInterval(this.torneiosRelogio);
        this.torneiosRelogio = null;
      }
    },

    async carregarTorneios() {
      try {
        await this.$store.dispatch('competition/fetchAllCompetitions');
        for (const torneio of this.competitions || []) {
          await this.$store.dispatch('competition/fetchCompetitionRanking', torneio.id);
        }
      } catch (error) {
        console.error('Erro ao carregar torneios:', error);
      }
    },

    rankingDoTorneio(id) {
      return this.getCompetitionRanking(id) || [];
    },

    imagemDoEvento(nome) {
      return imagemDeEvento(nome);
    },

    /**
     * Nome e descrição do evento pelo dicionário (`events.<nome>.<campo>`), com o texto do
     * banco como fallback — a mesma regra do `CalendarEvents` ao lado. Sem isto a agenda
     * aparecia traduzida e a ficha, na mesma página, em português.
     */
    textoDoEvento(nome, campo, padrao) {
      if (!nome) return padrao;
      const chave = `events.${nome}.${campo}`;
      const texto = this.$t(chave);
      return texto === chave ? padrao : texto;
    },

    diasRestantes(fim) {
      return Math.max(0, Math.ceil((new Date(fim) - new Date()) / 86400000));
    },

    formatoDeOuro(valor) {
      return new Intl.NumberFormat('pt-BR').format(valor || 0);
    },

    async carregarConquistas() {
      try {
        const conquistas = await v2.conquistas(this.$store.state.player.id);
        this.conquistasAlcancadas = conquistas.filter((c) => c.alcancada).map((c) => c.nome);
      } catch (error) {
        console.error('Erro ao carregar conquistas:', error);
      } finally {
        this.conquistasCarregadas = true;
      }
    },

    virarPagina(sentido) {
      const atual = this.indicePagina;
      const nova =
        sentido === 'dir'
          ? Math.min(atual + this.passo, this.totalDePaginas - 1)
          : Math.max(atual - this.passo, 0);
      this.paginas = { ...this.paginas, [this.fita]: nova };
    },

    /* -------------------------------------------------- mochila e cantinho */

    /** Os dois toques do D11: tocar no item levanta (os encaixes válidos acendem), tocar de novo larga. */
    tocarNaEntrada(entrada) {
      this.selecionada = this.selecionada?.chave === entrada.chave ? null : entrada;
      if (entrada.alvo) {
        arrasto.tocarItem({
          item: entrada,
          origem: entrada.chave,
          alvosValidos: [entrada.alvo],
          aoSoltar: this.aoSoltarNoEncaixe,
        });
      } else {
        arrasto.largar();
      }
    },

    /** Duplo clique equipa direto (D17) — o atalho de quem já sabe o que quer. */
    equiparDireto(entrada) {
      if (entrada.tipo === 'apetrecho' && !entrada.equipado) {
        this.selecionada = entrada;
        this.agirNoApetrecho(entrada);
        return;
      }
      if (!entrada.alvo || entrada.equipado) return;
      this.selecionada = entrada;
      this.equipar(entrada, entrada.alvo);
    },

    tocarNoLugar(lugar) {
      // Se algo está na mão e este encaixe aceita, o toque aqui é o SEGUNDO toque: equipa.
      if (lugar.equipavel && arrasto.aceita(lugar.slot)) {
        arrasto.soltar(lugar.slot);
        return;
      }
      // Senão, é leitura: a ficha da peça em uso, com o "Guardar" (desequipar).
      if (lugar.item) {
        this.selecionada = {
          chave: `equipado-${lugar.slot}`,
          tipo: lugar.equipavel ? 'equipado' : 'isca',
          slot: lugar.slot,
          alvo: null,
          equipado: true,
          quantidade: lugar.quantidade,
          bruto: lugar.item,
          item: lugar.item,
        };
      }
    },

    pressionar(evento, entrada) {
      if (!entrada.alvo) return; // consumível não se arrasta para encaixe nenhum
      arrasto.aoPressionar(evento, {
        item: entrada,
        origem: entrada.chave,
        alvosValidos: [entrada.alvo],
        aoSoltar: this.aoSoltarNoEncaixe,
      });
    },
    moverArrasto(evento) {
      arrasto.aoMover(evento);
    },
    largarArrasto(evento) {
      arrasto.aoLargar(evento);
    },
    aceita(slot) {
      return arrasto.aceita(slot);
    },
    ehOrigem(chave) {
      return arrasto.ehOrigem(chave);
    },

    aoSoltarNoEncaixe({ item: entrada, alvo }) {
      this.equipar(entrada, alvo);
    },

    async equipar(entrada, alvo) {
      const despacho = {
        vara: ['player/equipRod', { rodId: entrada.bruto.id }],
        barco: ['player/equipBoat', { boatId: entrada.bruto.id }],
        casa: ['player/equipHouse', { houseId: entrada.bruto.id }],
      }[alvo];
      if (!despacho) return;

      this.agindo = true;
      try {
        await this.$store.dispatch(despacho[0], despacho[1]);
        this.selecionada = null;
      } catch (error) {
        console.error('Erro ao equipar:', error);
      } finally {
        this.agindo = false;
      }
    },

    /** De 'vara'/'barco'/'casa' para o slot que o V2 fala (`rod`/`boat`/`house`). */
    slotDoServidor(nome) {
      return { vara: 'rod', barco: 'boat', casa: 'house' }[nome];
    },

    async guardar(slot) {
      this.agindo = true;
      try {
        await this.$store.dispatch('player/unequipSlot', this.slotDoServidor(slot));
        this.selecionada = null;
      } catch (error) {
        console.error('Erro ao guardar a peça:', error);
      } finally {
        this.agindo = false;
      }
    },

    async agir() {
      const e = this.selecionada;
      if (!e || this.agindo) return;

      if (e.tipo === 'equipado') {
        await this.guardar(e.slot);
        return;
      }

      if (e.alvo) {
        // A peça já em uso "Guarda" (desequipa); a guardada equipa. O rótulo do botão diz o mesmo.
        if (e.equipado) await this.guardar(e.tipo);
        else this.equipar(e, e.alvo);
        return;
      }

      if (e.tipo === 'apetrecho') {
        await this.agirNoApetrecho(e);
        return;
      }

      if (e.tipo === 'pocao') {
        await this.beberPocao(e);
        return;
      }

      if (e.tipo === 'tonic') {
        this.agindo = true;
        try {
          const resultado = await this.$store.dispatch('player/useTonic', e.bruto.id);
          if (resultado?.success) {
            await this.$store.dispatch('SoundController/playSound', { category: 'premiumItens', type: 'drinkTonic' });
          }
          this.selecionada = null;
        } catch (error) {
          console.error('Erro ao usar o tônico:', error);
        } finally {
          this.agindo = false;
        }
        return;
      }

      if (e.tipo === 'lootbox') {
        this.bau = { aberto: true, abrindo: false, premio: null, entrada: e };
      }
    },

    /**
     * Equipa a pilha (a unidade mais antiga dela) ou tira a equipada do encaixe.
     *
     * A mesma ação do componente `Apetrechos`, e não uma cópia da chamada: a store devolve o baú
     * inteiro, e o encaixe ao lado e a grade daqui se redesenham a partir da mesma resposta.
     */
    async agirNoApetrecho(e) {
      this.agindo = true;
      try {
        if (e.equipado) await this.$store.dispatch('pescador/desequiparApetrecho', e.bruto.slot);
        else await this.$store.dispatch('pescador/equiparApetrecho', e.bruto.unidades[0]);
        this.selecionada = null;
      } catch (error) {
        console.error('Erro no apetrecho:', error);
      } finally {
        this.agindo = false;
      }
    },

    /** Beber daqui é o mesmo que beber na tela de pesca — inclusive a recusa da energia cheia. */
    async beberPocao(e) {
      this.agindo = true;
      try {
        await this.$store.dispatch('pescador/beberPocao', e.bruto.potionId);
        this.$store.dispatch('avisos/avisar', {
          chave: 'exploration.pocoes.bebida',
          parametros: { nome: e.bruto.nome },
          icone: 'flask',
        });
        this.selecionada = null;
      } catch (error) {
        const codigo = error?.response?.data?.codigo;
        this.$store.dispatch('avisos/avisar', {
          chave: codigo === 'sem_efeito' ? 'exploration.pocoes.energiaCheia' : 'exploration.errors.generico',
          icone: 'flask',
          tom: 'ruim',
        });
      } finally {
        this.agindo = false;
      }
    },

    async abrirBau() {
      if (!this.bau.entrada || this.bau.abrindo) return;
      this.bau.abrindo = true;
      await this.$store.dispatch('SoundController/playSound', { category: 'premiumItens', type: 'chestOpen' });
      try {
        const resultado = await this.$store.dispatch('player/openLootBox', this.bau.entrada.bruto.id);
        if (resultado?.success && resultado.reward) {
          this.bau.premio = resultado.reward;
          await this.$store.dispatch('SoundController/playSound', { category: 'premiumItens', type: 'getReward' });
        } else {
          this.fecharBau();
        }
      } catch (error) {
        console.error('Erro ao abrir o baú:', error);
        this.fecharBau();
      } finally {
        this.bau.abrindo = false;
      }
    },
    fecharBau() {
      this.bau = { aberto: false, abrindo: false, premio: null, entrada: null };
      this.selecionada = null;
    },

    /* ------------------------------------------------------------- coleção */

    paginaDePeixes(indice) {
      return this.estante.slice(indice * PEIXES_POR_PAGINA, (indice + 1) * PEIXES_POR_PAGINA);
    },
    paginaDeConquistas(indice) {
      return this.conquistas.slice(indice * SELOS_POR_PAGINA, (indice + 1) * SELOS_POR_PAGINA);
    },
    nomeDoPeixe(peixe) {
      const chave = `fishes.${peixe.name}`;
      const traduzido = this.$t(chave);
      return traduzido === chave ? peixe.name : traduzido;
    },

    /** O `maps.name` do banco para o nome traduzido. A tabela mora em `utils/mapNames`. */
    nomeDoMapa(nome) {
      const chave = `maps.mapNames.${getMapKey(nome)}`;
      const traduzido = this.$t(chave);
      return traduzido === chave ? nome : traduzido;
    },

    /**
     * A estante inteira, uma vez por visita à fita.
     *
     * Não recarrega a cada peixe pescado de propósito: o jogador não pesca com o caderno aberto, e
     * a próxima abertura da tela já traz a contagem em dia. O `locale` vai junto porque o texto
     * mora no banco, por idioma.
     */
    async carregarEnciclopedia() {
      this.carregandoEnciclopedia = true;
      try {
        this.enciclopedia = await v2.enciclopedia(this.$store.state.player.id, this.$i18n.locale);
        this.enciclopediaCarregada = true;
      } catch (error) {
        console.error('Erro ao carregar a enciclopédia:', error);
      } finally {
        this.carregandoEnciclopedia = false;
      }
    },

    /**
     * Abre a entrada de um peixe.
     *
     * A folha abre ANTES da resposta, com o estado de carregando dentro: abrir depois faria o
     * toque parecer perdido nos 200 ms da rede. Só chega aqui peixe descoberto — a silhueta nem
     * emite o evento.
     */
    async abrirEntrada(peixe) {
      this.entrada = { aberta: true, carregando: true, dados: null };
      try {
        const dados = await v2.entradaDaEnciclopedia(
          this.$store.state.player.id,
          peixe.id,
          this.$i18n.locale,
        );
        this.entrada.dados = {
          ...dados,
          // O nome vem do i18n, como na estante: o servidor manda o do catálogo, em português.
          nomeExibido: this.nomeDoPeixe({ name: dados.nome }),
          // Mesma queda da estante: `imagem` é a mídia do painel e é nula enquanto ninguém subiu
          // arte; o desenho do bundle é resolvido pelo nome do catálogo, que aqui sempre existe.
          arte: dados.imagem || getFishImage(dados.nome),
        };
      } catch (error) {
        console.error('Erro ao abrir a entrada:', error);
        this.fecharEntrada();
      } finally {
        this.entrada.carregando = false;
      }
    },

    fecharEntrada() {
      this.entrada = { aberta: false, carregando: false, dados: null };
    },

    /**
     * 8.7 — a entrada leva à carta náutica.
     *
     * Vai para a carta e não para o mapa específico: a carta é uma rota, e ela já abre na parada
     * onde o barco está. Levar direto a um ponto que o jogador ainda não liberou seria mostrar a
     * porta trancada sem o caminho até ela.
     */
    irParaOMapa() {
      this.fecharEntrada();
      this.$router.push('/maps');
    },
  },
};
</script>

<style scoped>
.caderno {
  min-height: 100dvh;
}

.caderno__interior {
  max-width: 980px;
  margin: 0 auto;
  /* A folha ativa do rodapé (99px) mais a faixa com o nome: com 64px as setas da enciclopédia
   * ficavam atrás do rótulo "Inventário" no telefone (relato de 23/09). `e-6` e não `e-5`: com
   * 24px as setas sobravam só 2px acima da faixa, medido a 360px. */
  padding: var(--e-5) var(--e-3) calc(var(--altura-barra) + var(--e-6));
}

/* O título no PERGAMINHO do Paper Pack (papel.css) — tinta sobre papel. */
.caderno__topo {
  text-align: center;
  margin-bottom: var(--e-5);
}

.caderno__titulo {
  font-family: var(--fonte-display);
  font-size: var(--f-5);
  line-height: var(--lh-curto);
  margin: 0;
}

@media (max-width: 599px) {
  .caderno__titulo {
    font-size: var(--f-3);
  }
}

.caderno__livro {
  justify-content: center;
}

/**
 * ================================ O CADERNO EM TAMANHO DE DESKTOP ================================
 *
 * O veto: *"está pequeno demais no desktop, em telas fullhd praticamente some"* (autor, 03/09).
 * Ele estava certo, e a causa não era o `max-width: 1008px` sozinho — era a soma de quatro coisas
 * que **gastavam altura**, que é o eixo escasso: uma tela 1920×1080 é 1,98:1 e o livro é 1,29:1,
 * então largura sobra e altura não. Numa janela típica de 1080p sobram ~969px de viewport e:
 *
 * | quem | quanto | o que se fez |
 * |---|---|---|
 * | cabeçalho fixo | 67px | o livro passa POR BAIXO dele — a faixa morta cabe lá |
 * | título em fluxo | ~93px | saiu do fluxo: é absoluto, e pousa na mesma faixa morta |
 * | faixa morta da arte | ~20% da caixa | deixou de ser altura gasta e virou o lugar dos dois acima |
 * | barra de navegação | 132px | **essa fica reservada** — o papel dela é opaco e cobriria a página |
 *
 * O resultado é um livro de ~1070px de largura onde havia ~850, e que continua crescendo: 2× exato
 * (1344px) numa tela 1440p. O truque todo depende da medida do PNG (tinta só a partir de `y=103`
 * de 520 — ver o comentário de `.fish-book__arte`); recortar a arte de novo obriga a remedir.
 *
 * A barra é a única coisa que **não** se invade, e não é conservadorismo: as seis folhas são papel
 * opaco, centralizadas, e comeriam o pé das duas páginas — que é conteúdo, não moldura.
 */
@media (min-width: 900px) {
  .caderno {
    /* Sobe por baixo do cabeçalho: o `.main-content` reserva `--altura-cabecalho` para todas as
     * telas, e esta devolve o espaço porque tem 100px de arte transparente para pôr ali. Os dois
     * margins são adjacentes e colapsam para zero — a cena passa a começar no topo da janela, o
     * que também tira a tarja do fundo do body que aparecia acima dela. */
    margin-top: calc(-1 * var(--altura-cabecalho));
  }

  .caderno__interior {
    position: relative;
    /* O livro se mede pela altura (ver `--livro-reserva`); a largura aqui só não pode estrangulá-lo
     * antes disso — 980px estrangulava. */
    max-width: 1560px;
    padding-top: 0;
    /* O respiro do pé é a barra: sem ele, uma janela baixa esconde o fim do livro atrás das folhas
     * em vez de rolar até ele. */
    padding-bottom: var(--altura-barra);
  }

  /* O título sai do fluxo e vai para a faixa morta, ancorado ABAIXO do cabeçalho — a âncora é em
   * pixel e não em porcentagem porque o cabeçalho também é, e é ele que o título não pode cruzar. */
  .caderno__topo {
    position: absolute;
    top: calc(var(--altura-cabecalho) + var(--e-6));
    left: 0;
    right: 0;
    margin-bottom: 0;
    z-index: 1;
    /* O pergaminho é decoração de topo; clique nele é clique no que estiver embaixo. */
    pointer-events: none;
  }

  .caderno__livro {
    /* 2× exato do recorte: escala inteira, o único fator em que o pixel não deforma. */
    --livro-largura-max: 1344px;
    /* O que já foi gasto de altura antes do livro. O cabeçalho NÃO entra: ele fica sobre a faixa
     * morta. A barra entra inteira, e o `--e-2` é o respiro entre o pé do livro e ela. */
    --livro-reserva: calc(var(--altura-barra) + var(--e-2));
    /* As fitas e as setas começam abaixo do cabeçalho, não no topo da caixa (que agora está atrás
     * dele). O `--e-7` alinha a primeira fita com o alto do papel. */
    --livro-recuo-do-topo: calc(var(--altura-cabecalho) + var(--e-7));
  }
}

.caderno__pagina {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

/*
 * NO TELEFONE AS DUAS PÁGINAS EMPILHAM na mesma folha (`.fish-book__pagina--unica`), e cada uma
 * tinha `height: 100%` — da folha inteira. A primeira transbordava a própria caixa e a segunda
 * começava exatamente onde a caixa terminava, POR CIMA do transbordo: no calendário, tocar num
 * evento abria o detalhe dele sobre a agenda, com os dois textos um em cima do outro (relatado em
 * 28/09/2026). Empilhadas, cada página mede o próprio conteúdo, e a folha é quem rola.
 */
.fish-book__pagina--unica > .caderno__pagina {
  height: auto;
  flex-shrink: 0;
}

.fish-book__pagina--unica > .caderno__pagina + .caderno__pagina {
  margin-top: var(--e-4);
}

.caderno__rotulo {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

/* ------------------------------------------------------------------ cantinho */

.caderno__cantinho {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--e-2);
}

.caderno__lugar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.caderno__lugar-nome {
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.caderno__ficha {
  border-top: 2px dashed var(--papel-sombra);
  padding-top: var(--e-1);
  min-height: 72px;
}

/*
 * A página é uma coluna flex de altura FIXA (a do livro), e filho de flex encolhe por padrão: com
 * a ficha aberta, ela encolhia até os 72px do `min-height` e o conteúdo transbordava por cima das
 * notas — o botão Equipar/Beber cobria a linha "Poder de Pesca". Quem rola é a página do livro;
 * os filhos ficam com a altura que o conteúdo pede. A raiz do `Apetrechos` recebe o escopo daqui.
 */
.caderno__cantinho,
.caderno__ficha,
.caderno__notas,
.caderno__pagina > .apetrechos {
  flex-shrink: 0;
}

.caderno__ficha-nome {
  margin: 0 0 var(--e-1);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta);
}

.caderno__ficha-meta {
  list-style: none;
  margin: 0 0 var(--e-2);
  padding: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.caderno__ficha-vazia {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  font-style: italic;
}

/* Quando a dica é o único conteúdo da página, ela desce um pouco da borda — sem centralizar no
 * meio, que o autor vetou (03/09). */
.caderno__pagina > .caderno__ficha-vazia {
  margin-top: var(--e-3);
}

.caderno__notas {
  list-style: none;
  margin: 0;
  padding: var(--e-2) 0 0;
  border-top: 2px dashed var(--papel-sombra);
}

.caderno__notas li {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  padding: 2px 0;
}

.caderno__notas strong {
  color: var(--tinta);
}

/* ------------------------------------------------------------------- coleção */

/*
 * A GRADE DA ESTANTE NÃO MORA AQUI. `PaginaDaEnciclopedia` é componente local de função de
 * render, igual ao `PaginaDeRanking`, e o atributo de escopo não alcança os filhos dele: as
 * regras ficaram escritas e mudas, e a silhueta do peixe por descobrir nunca chegou na tela — o
 * catálogo aparecia colorido e transbordando a folha. Estão no `<style>` sem `scoped`, no fim
 * do arquivo, presas ao `.fish-book`.
 */

/* No desktop os selos crescem junto com o livro (1008px) — ver a arte do peixe lá embaixo. */
@media (min-width: 1280px) {
  .caderno__selos img {
    width: 64px;
    height: 64px;
  }

  .caderno__pagina {
    gap: var(--e-3);
  }
}

/* O contador de descobertos, no rótulo da página: "12 / 30". Empurrado para a direita porque o
 * rótulo é a linha do título e o número é a resposta dele. */
.caderno__contador {
  float: right;
  color: var(--latao);
}

/* A página da direita quando nada foi escolhido: o convite fica centrado na folha, e não
 * colado no topo — meia página de papel em branco com uma linha no alto parece falha. */
.enciclopedia__convite {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
}

/* O voltar do telefone mora no rótulo da página, no lugar do título. É o gesto do caderno —
 * virar a folha de volta — e não um X de modal, porque não há modal. */
.caderno__voltar {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: var(--latao);
  cursor: pointer;
}

/* ---------------------------------------------------------------- conquistas */

.caderno__selos {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.caderno__selos li {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding-bottom: var(--e-2);
  border-bottom: 1px dashed var(--papel-sombra);
}

.caderno__selos img {
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex-shrink: 0;
}

/* O selo não conquistado fica a lápis: visível — dá vontade — mas sem a tinta. */
.caderno__selo--fechado img {
  filter: grayscale(1);
  opacity: 0.35;
}

.caderno__selo--fechado .caderno__selo-nome {
  color: var(--tinta-fraca);
}

.caderno__selo-nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta);
}

.caderno__selo-descricao {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* -------------------------------------------------- calendário e torneios */

.caderno__evento-arte {
  width: 100%;
  max-height: 140px;
  object-fit: cover;
  image-rendering: auto;
  border: 3px solid var(--madeira);
}

.caderno__evento-texto {
  margin: var(--e-2) 0;
  font-size: var(--f-2);
  color: var(--tinta);
}

/* A página de continuação (o 2º cartaz da dupla) desce o mesmo tanto que o rótulo da esquerda
 * ocupa, para os dois cartazes alinharem pelo topo. */
.caderno__pagina--continuacao {
  padding-top: calc(var(--f-2) * 1.5 + var(--e-1) + var(--e-2) + 2px);
}

/* ------------------------------------------------------------------ fantasma */

.caderno__fantasma {
  position: fixed;
  z-index: 60;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.caderno__fantasma img {
  width: 56px;
  height: 56px;
  object-fit: contain;
  image-rendering: pixelated;
  filter: drop-shadow(0 4px 0 rgba(0, 0, 0, 0.35));
}

/* ----------------------------------------------------------------------- baú */

.caderno__bau {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-3);
  padding: var(--e-3) 0;
}

.caderno__bau-fechado {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-2);
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta);
}

.caderno__bau-fechado img,
.caderno__bau-premio img {
  width: 128px;
  height: 128px;
  object-fit: contain;
  image-rendering: pixelated;
}

.caderno__bau-fechado:disabled {
  opacity: 0.6;
  cursor: wait;
}

.caderno__bau-premio {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-2);
  width: 100%;
}

.caderno__bau-nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-3);
  color: var(--tinta);
  text-align: center;
}

.caderno__bau-quanto {
  margin: 0;
  color: var(--latao);
}
</style>

<style>
/**
 * O diário de bordo (ranking) SEM scoped: `PaginaDeRanking` é um componente local de função de
 * render, e o atributo de escopo da página não alcança os filhos dele — nem com `:deep`, que
 * precisa de uma raiz no template desta página. As classes são namespaced e o seletor fica preso
 * ao `.fish-book`, então o vazamento é nulo na prática.
 */
.fish-book .caderno__posicoes {
  list-style: none;
  margin: 0;
  padding: 0;
}

.fish-book .caderno__posicoes li {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  padding: var(--e-1) 0;
  border-bottom: 1px dashed var(--papel-sombra);
  font-size: var(--f-1);
}

.fish-book .caderno__posicao-numero {
  min-width: 3ch;
  color: var(--tinta-fraca);
}

.fish-book .caderno__posicao--laureada .caderno__posicao-numero {
  color: var(--latao);
  font-weight: 700;
}

.fish-book .caderno__posicao-nome {
  flex: 1;
  text-align: left;
  color: var(--tinta);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fish-book .caderno__posicao-valor {
  color: var(--tinta-fraca);
}

/* O rótulo do PaginaDeRanking, espelho do .caderno__rotulo escopado acima. */
.fish-book .caderno__posicoes-rotulo {
  margin: 0 0 var(--e-2);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

/**
 * A ESTANTE DA ENCICLOPÉDIA, pela mesma razão do diário de bordo acima: `PaginaDaEnciclopedia`
 * é função de render local e não herda o `data-v` da página. Enquanto estas regras viveram no
 * bloco escopado, a grade inteira ficou sem estilo — arte em tamanho natural vazando a folha — e
 * o peixe por descobrir aparecia colorido, entregando de graça justo o que a silhueta esconde.
 */

/* O caderno de naturalista (D14): o desenho do peixe vai DIRETO no papel — sem encaixe escuro —
 * com o nome e a medida a lápis embaixo, sobre uma linha pontilhada de ficha de campo. */
.fish-book .caderno__peixes {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--e-3) var(--e-2);
  align-content: start;
  flex: 1;
}

.fish-book .caderno__peixes li {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

.fish-book .caderno__peixe-arte {
  width: 96px;
  height: 72px;
  object-fit: contain;
  image-rendering: auto;
}

/* No desktop os desenhos crescem junto com o livro (1008px) — "pequeno demais para pc" foi veto
 * do autor (03/09). Os selos crescem junto, na media query escopada lá em cima. */
@media (min-width: 1280px) {
  .fish-book .caderno__peixe-arte {
    width: 150px;
    height: 110px;
  }
}

.fish-book .caderno__peixe-nome {
  margin: var(--e-1) 0 0;
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta);
  text-align: center;
}

.fish-book .caderno__peixe-medida {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  text-align: center;
}

/* O desenho inteiro é o alvo do toque, e não só a arte: 44px de mínimo (D11) vêm de graça porque
 * a célula já é maior que isso. Botão sem cara de botão — o papel não tem relevo. */
.fish-book .caderno__peixe-botao {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}

/* A silhueta não abre nada: sem cursor de mão, sem foco, sem promessa. */
.fish-book .caderno__peixe-botao--mudo {
  cursor: default;
}

@media (hover: hover) {
  .fish-book .caderno__peixe-botao:hover .caderno__peixe-nome {
    color: var(--latao);
  }
}

/* O peixe cuja ficha está aberta na outra página: uma marca de lápis embaixo do desenho, para
 * que a grade e a ficha sejam visivelmente a mesma coisa. */
.fish-book .caderno__peixe-botao--aberto .caderno__peixe-nome {
  color: var(--latao);
  text-decoration: underline dashed;
}

/**
 * A SILHUETA DO PEIXE POR DESCOBRIR (8.5, D14).
 *
 * `brightness(0)` chapa a arte em preto **preservando o alfa** — é a forma, não um retângulo. A
 * opacidade a traz para o tom de lápis do papel, para o caderno não virar um tabuleiro de manchas
 * pretas: são 30 peixes e no começo quase todos estão fechados.
 *
 * Silhueta e não espaço vazio porque vazio não dá curiosidade: a forma diz que existe algo com
 * aquele tamanho e aquele feitio, e os mapas embaixo dizem onde procurá-lo.
 */
.fish-book .caderno__peixe-arte--silhueta {
  filter: brightness(0);
  opacity: 0.28;
}
</style>
